import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';

import { getLocumName } from './AdminPickers';
import {
  PII_STATUS_LABELS, formatDateLabel, formatDurationHours, formatMoney, formatPercent, formatServerErrors, getRowPhase, getRowProblems, getRowTotalCents, getRowWarning,
} from './adminShiftUtils';

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const PHASE_LABELS = { ENDED: 'Ended', IN_PROGRESS: 'In progress', UPCOMING: 'Upcoming' };

const PhaseBadge = ({ phase }) => (
  phase ? <span className={`badge phase-${phase.toLowerCase().replace('_', '-')}`}>{PHASE_LABELS[phase]}</span> : null
);

/** A checkbox that spells out exactly what happens when it is on and when it is off. */
export const ToggleField = ({ label, checked, onChange, disabled, onText, offText, disabledText, className }) => (
  <label className={`toggle-field ${disabled ? 'disabled' : ''} ${className || ''}`}>
    <input type={'checkbox'} checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
    <span className={'toggle-body'}>
      <span className={'toggle-label'}>{label}</span>
      { disabled && disabledText && <span className={'toggle-line'}>{disabledText}</span> }
      <span className={`toggle-line ${checked ? 'current' : ''}`}><strong>{'On: '}</strong>{onText}</span>
      <span className={`toggle-line ${checked ? '' : 'current'}`}><strong>{'Off: '}</strong>{offText}</span>
    </span>
  </label>
);

export const HelpPanel = ({ isBusiness }) => (
  <details className={'help-panel'} open>
    <summary>{'How this page works (read before your first booking)'}</summary>
    <div className={'help-content'}>
      <h3>{'What it does'}</h3>
      <p>
        {'Creates shifts and books a locum onto them as if the locum had requested each shift and the store had accepted. '}
        {'Use it for work arranged outside Locumly, including shifts that have already happened.'}
      </p>
      <p>
        <strong>{'One booking can cover many stores and dates. '}</strong>
        {'Add shifts for one store, then pick another store and add more, as many times as you like (up to 100 shifts), including different dates, times and rates per store. Nothing is booked until you click Review booking and confirm, and then every shift is booked together for the one locum.'}
      </p>
      <h3>{'What happens once you confirm'}</h3>
      <ul>
        <li><strong>{'Shifts that have already ended: '}</strong>{'show on the locum\'s dashboard with "Mark Shift As Complete". The locum marks them complete, leaves a review and invoices exactly like a normally requested shift (Stripe invoice to the store for the shift total plus the store\'s Locumly fee, and the store is told it has a payment).'}</li>
        <li><strong>{'Upcoming shifts: '}</strong>{'show in the locum\'s schedule as booked, with a calendar link. About 24 hours before the start, the system sends its automatic shift reminder (see "Stores" below). Once the shift ends the locum gets the usual "mark complete" reminder, then completes and invoices as normal.'}</li>
        <li><strong>{'Stores: '}</strong>{'the shifts appear under the store as filled by this locum. Nobody at the store is told about the booking itself. However, the automatic 24 hour reminder for an upcoming shift also goes to every business linked to the store (in-app, SMS and email, including this account), and it cannot be switched off here.'}</li>
        <li><strong>{'Status: '}</strong>{'"Confirmed" when the locum has a provider number on file for that store, otherwise "Filled". Both complete and invoice the same way.'}</li>
        <li><strong>{'Shifts starting soon: '}</strong>{'a shift starting within 24 hours of booking gets the reminder (locum and store) within about 5 minutes.'}</li>
        <li><strong>{'All or nothing: '}</strong>{'every shift is saved in one go. If anything fails, nothing is created and the reasons are shown here.'}</li>
        <li><strong>{'No undo button: '}</strong>{'check the review screen carefully. To reverse a booking, ask engineering (booked shifts carry an admin assignment marker).'}</li>
      </ul>
      <h3>{'Good to know'}</h3>
      <ul>
        <li>{'Times are the store\'s local time, in the store\'s time zone, whatever time zone your computer is in.'}</li>
        <li>{'Total = paid hours x hourly rate, rounded to whole dollars, where paid hours = shift length minus the unpaid break. The Total column shows it for each row.'}</li>
        { isBusiness
          ? <li>{'Only your own stores can be picked, and only locums who have worked at one of your stores appear in the locum search. Everything else works the same as the options below.'}</li>
          : <li>
          {'Any store can be picked. Stores marked "Not linked yet" are linked to the admin account automatically when you book (the store is added to the account and the account to the store, in the same save). From then on this account also receives that store\'s business notifications, such as shift reminders, shift requests and payment notices, and can edit or cancel its shifts. Log out and back in to see newly linked stores in the local store list.'}
        </li>
        }
        <li>{'The locum does not need ABN, AHPRA or PII on file to be booked here, but they are needed to invoice. A warning shows if any are missing.'}</li>
        <li>{'Blocked automatically: overlapping shifts for the locum, a shift already existing at that store and time, and shifts ended more than 29 days ago (see the option below).'}</li>
        <li>{'The locum\'s own pending requests that overlap a new upcoming shift are removed, as happens when they are accepted normally.'}</li>
      </ul>
    </div>
  </details>
);

const NOT_ON_FILE = 'Not on file';

/** Label/value grid. A missing value shows "Not on file" in red when `required` is set, otherwise as muted text. */
const DetailGrid = ({ items }) => (
  <dl className={'detail-grid'}>
    { items.map(({ label, value, required, warn }) => (
      <div className={'detail-item'} key={label}>
        <dt>{label}</dt>
        <dd className={(!value && required) || warn ? 'warn' : (!value ? 'muted' : '')}>{value || NOT_ON_FILE}</dd>
      </div>
    ))}
  </dl>
);

export const LocumCard = ({ locum }) => {
  const missing = locum.missingCredentials || [];
  const piiPassing = locum.piiStatus === 'SELF_DECLARED' || locum.piiStatus === 'VERIFIED';
  return (
    <section className={'detail-card locum-card'}>
      <div className={'detail-card-title'}>{getLocumName(locum)}</div>
      <DetailGrid
        items={[
          { label: 'Type', value: locum.type },
          { label: 'Email', value: locum.emailAddress, required: true },
          { label: 'Phone', value: locum.phoneNumber },
          { label: 'ABN', value: locum.abn, required: true },
          { label: 'AHPRA', value: locum.ahpra, required: true },
          { label: 'PII (professional indemnity)', value: PII_STATUS_LABELS[locum.piiStatus] || locum.piiStatus, warn: !piiPassing },
          { label: 'Payments (Stripe) account', value: locum.hasStripeAccount ? 'Connected' : null, required: true },
          { label: 'GST', value: locum.gstFree ? 'GST free' : 'GST applies' },
        ]}
      />
      { missing.length > 0 &&
        <div className={'inline-warning'}>
          {`Missing ${missing.join(', ')}. They could not request shifts themselves, so check they can still complete and invoice. ABN is printed on Locumly-generated invoices.`}
        </div>
      }
      { !locum.hasStripeAccount && <div className={'inline-warning'}>{'No payments (Stripe) account yet. They cannot be paid until they connect one.'}</div> }
    </section>
  );
};

/** Store details from toStoreSummary (before preview) or the server's store summary (preview and result). */
export const StoreCard = ({ store, showProviderNumber }) => (
  <section className={'detail-card store-card'}>
    <div className={'detail-card-title'}>{store.name}</div>
    <DetailGrid
      items={[
        { label: 'Type', value: store.type },
        { label: 'Address', value: store.address, required: true },
        { label: 'Time zone', value: store.timeZone, required: true },
        { label: 'Registered business', value: store.registeredBusinessName, required: true },
        { label: 'Trading as', value: store.tradingAs },
        { label: 'ABN', value: store.abn, required: true },
        { label: 'ACN', value: store.acn },
        { label: 'Locumly fee on invoice', value: formatPercent(store.commissionRate) },
        { label: 'Bank details', value: store.hasBankDetails ? 'On file' : null },
        { label: 'Locum onboarding document', value: store.hasOnboardingDocument ? 'Attached to confirmation email' : null },
        { label: 'Store contact email', value: store.contactEmailAddress },
        ...(showProviderNumber ? [{ label: 'Locum provider number', value: store.providerNumber, warn: !store.providerNumber }] : []),
      ]}
    />
    { (store.linked === false || store.willBeLinked) && <div className={'inline-warning'}>{'Not linked to the admin account yet. Booking links it automatically, and from then on this account also receives the store\'s business notifications (shift reminders, shift requests and payment notices).'}</div> }
    { showProviderNumber && !store.providerNumber && <div className={'inline-warning'}>{'No provider number on file for this locum at this store. Their shifts are booked as Filled instead of Confirmed.'}</div> }
    { !store.registeredBusinessName && <div className={'inline-warning'}>{'No registered business name. Invoices fall back to the store name.'}</div> }
  </section>
);

/** One editable row. Memoised so typing in one row, or in the builder above, does not re-render every row. */
const ShiftRow = React.memo(({ row, index, onChange, onRemove, readOnly }) => {
  const problems = getRowProblems(row);
  return (
    <tr className={problems.length > 0 ? 'has-problem' : ''}>
      <td>{index + 1}</td>
      <td className={'store-cell'}>{row.storeName}</td>
      <td className={'date-cell'}>
        {formatDateLabel(row.date)}
        <PhaseBadge phase={getRowPhase(row)} />
      </td>
      <td><input type={'time'} value={row.start} disabled={readOnly} onChange={event => onChange(row.id, { start: event.target.value })} /></td>
      <td><input type={'time'} value={row.end} disabled={readOnly} onChange={event => onChange(row.id, { end: event.target.value })} /></td>
      <td><input className={'narrow'} type={'number'} min={0} step={5} value={row.unpaidBreakMinutes} disabled={readOnly} onChange={event => onChange(row.id, { unpaidBreakMinutes: event.target.value })} /></td>
      <td><input className={'narrow'} type={'number'} min={0} step={0.5} value={row.rate} disabled={readOnly} onChange={event => onChange(row.id, { rate: event.target.value })} /></td>
      <td><input type={'checkbox'} checked={row.negotiable} disabled={readOnly || row.billToMedicare} onChange={event => onChange(row.id, { negotiable: event.target.checked })} /></td>
      <td>
        <input
          type={'checkbox'}
          checked={row.billToMedicare}
          disabled={readOnly}
          onChange={event => onChange(row.id, {
            billToMedicare: event.target.checked,
            ...(event.target.checked ? { negotiable: false, ...(row.rate === '' ? { rate: '0' } : {}) } : {}),
          })}
        />
      </td>
      <td className={'total-cell'}>
        {problems.length === 0 ? <>{formatMoney(getRowTotalCents(row))}<span className={'total-hours'}>{formatDurationHours(row)}</span>{getRowWarning(row) && <span className={'row-warning'}>{getRowWarning(row)}</span>}</> : <span className={'problem-text'}>{problems[0]}</span>}
      </td>
      <td>
        { !readOnly && <button type={'button'} className={'icon-button'} aria-label={`Remove row ${index + 1}`} onClick={() => onRemove(row.id)}>{'✕'}</button> }
      </td>
    </tr>
  );
});

export const ShiftRowsTable = ({ rows, onChange, onRemove, readOnly }) => (
  <div className={'rows-table-wrapper'}>
    <table className={'rows-table'}>
      <thead>
        <tr>
          <th>{'#'}</th>
          <th>{'Store'}</th>
          <th>{'Date'}</th>
          <th>{'Start'}</th>
          <th>{'End'}</th>
          <th>{'Break (min)'}</th>
          <th>{'Rate ($/h)'}</th>
          <th title={'Rate shown as negotiable. Label only, the rate entered is what is booked.'}>{'Negotiable'}</th>
          <th title={'The locum bills Medicare directly: no invoice is created when they complete the shift.'}>{'Medicare'}</th>
          <th>{'Total'}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        { rows.map((row, index) => <ShiftRow key={row.id} row={row} index={index} onChange={onChange} onRemove={onRemove} readOnly={readOnly} />) }
      </tbody>
    </table>
  </div>
);

export const OptionsPanel = ({ options, onChange, hasEndedRows, hasUpcomingRows, needsOldShiftOverride, needsDuplicateOverride }) => (
  <section className={'options-panel'}>
    <h3 className={'section-title'}>{'Messages and safety options'}</h3>
    <ToggleField
      label={'Notify the locum about their upcoming shifts'}
      checked={options.notifyLocum}
      disabled={!hasUpcomingRows}
      onChange={value => onChange({ notifyLocum: value })}
      disabledText={'Not available: every shift in the list has already ended.'}
      onText={'For each upcoming shift the locum gets the normal "You\'re confirmed" in-app notification, an SMS (if they have a phone number and have not turned off confirmation SMS) and the confirmation email. The email includes the calendar link, the store\'s onboarding document and the store\'s ABN and bank details. The store is not notified.'}
      offText={'No confirmation is sent. The shifts still appear in the locum\'s schedule with a calendar link, and the automatic 24 hour reminder before each shift still goes out to the locum and the store (this cannot be turned off here). Use this when you have already told the locum yourself.'}
    />
    <ToggleField
      label={'Skip the "mark your shift complete" reminder for ended shifts'}
      checked={options.suppressCompletionReminders}
      disabled={!hasEndedRows}
      onChange={value => onChange({ suppressCompletionReminders: value })}
      disabledText={'Not available: none of the shifts in the list have ended.'}
      onText={'The locum is not sent the automatic "Completed your shift?" SMS and email for the ended shifts. The "Mark Shift As Complete" button is still on their dashboard, but nothing prompts them, so tell them yourself.'}
      offText={'Within about 5 minutes of booking, the locum gets the automatic "Completed your shift?" SMS and email, one for each ended shift (10 ended shifts means 10 SMS and 10 emails). Turn this on to avoid that flood.'}
    />
    { needsOldShiftOverride &&
      <ToggleField
        className={'warning'}
        label={'Allow shifts that ended more than 29 days ago'}
        checked={options.allowOldShifts}
        onChange={value => onChange({ allowOldShifts: value })}
        onText={'Shifts that ended more than 29 days ago can be booked. Locumly auto-completes any unfinished shift 30 days after it ended (checked every 5 minutes): it raises only the Locumly fee invoice to the store, the locum is not asked to review, and the locum must send their own invoice for their pay. So these shifts will not go through the normal "Mark Shift As Complete" invoicing.'}
        offText={'Booking is blocked if any shift ended more than 29 days ago. Locumly auto-completes shifts 30 days after they end, so anything older would be turned into a fee-only invoice before the locum could complete it normally. Remove those rows, or turn this on only if the fee-only outcome is what you want.'}
      />
    }
    { needsDuplicateOverride &&
      <ToggleField
        className={'warning'}
        label={'Allow a new shift at the same time as an existing one'}
        checked={options.allowDuplicateShifts}
        onChange={value => onChange({ allowDuplicateShifts: value })}
        onText={'If a shift already exists at the same store, start and end time, it is left exactly as it is and a second, separate shift is created for this locum next to it. Check the existing shift\'s status: if it is still open to requests, the store will have two shifts for the same slot. The review screen lists each existing shift it found.'}
        offText={'Booking is blocked when a shift already exists at that store and time, and the existing shift id is shown. Remove the row, or turn this on if you want to keep the existing shift and add a new one beside it.'}
      />
    }
  </section>
);

/** Headline numbers at the top of the review and confirmation screens. */
const StatTiles = ({ tiles }) => (
  <div className={'stat-tiles'}>
    { tiles.map(tile => (
      <div key={tile.label} className={`stat-tile ${tile.tone || ''}`}>
        <span className={'stat-value'}>{tile.value}</span>
        <span className={'stat-label'}>{tile.label}</span>
        { tile.hint && <span className={'stat-hint'}>{tile.hint}</span> }
      </div>
    ))}
  </div>
);

/** A list of short points, each with a bold headline and a plain explanation underneath. */
const InfoList = ({ items }) => (
  <ul className={'info-list'}>
    { items.map((item, index) => (
      <li key={`${index}-${item.title || item.detail}`} className={`info-item ${item.tone || ''}`}>
        { item.title && <span className={'info-title'}>{item.title}</span> }
        <span className={'info-detail'}>{item.detail}</span>
      </li>
    ))}
  </ul>
);

export const PreviewStep = ({ preview, options, errors, submitting, onBack, onConfirm }) => {
  const { locum, stores, shifts, counts, warnings, storesWithoutProviderNumber, pendingRequestsToRemove, willNotifyCount, completionRemindersSuppressed } = preview;
  const now = Date.now();
  const startingSoon = shifts.filter(shift => shift.phase === 'UPCOMING' && shift.startTime - now <= DAY_IN_MILLISECONDS).length;
  const allWarnings = [
    ...formatServerErrors(warnings).map(message => ({ detail: message })),
    ...(startingSoon > 0 ? [{
      title: `${startingSoon} upcoming shift${startingSoon === 1 ? '' : 's'} start within 24 hours`,
      detail: 'Within about 5 minutes the locum and the store\'s businesses get the automatic shift reminder (in-app, SMS and email).',
    }] : []),
    ...(storesWithoutProviderNumber.length > 0 ? [{
      title: 'No provider number on file',
      detail: `Missing for this locum at: ${storesWithoutProviderNumber.join(', ')}. Those shifts are booked as Filled instead of Confirmed.`,
    }] : []),
    ...(options.allowOldShifts && shifts.some(shift => shift.phase === 'ENDED') ? [{
      title: 'Old shifts are allowed',
      detail: 'Any shift that ended over 30 days ago is auto-completed with a service-fee-only invoice within about 5 minutes.',
    }] : []),
  ];
  const totalValue = shifts.reduce((sum, shift) => sum + (shift.total || 0), 0);
  const whatWillHappen = [
    willNotifyCount > 0
      ? { tone: 'on', title: 'Locum notification: ON', detail: `${willNotifyCount} upcoming shift${willNotifyCount === 1 ? '' : 's'} will each send the locum an in-app notification, SMS and confirmation email.` }
      : { tone: 'off', title: 'Locum notification: OFF', detail: 'No confirmation message is sent to the locum.' },
    ...(counts.upcoming > 0 ? [{ title: '24 hour reminder (automatic)', detail: 'Upcoming shifts send the locum and every business linked to the store a reminder about 24 hours before the start. This cannot be turned off.' }] : []),
    ...(counts.ended > 0 ? [{ title: 'Ended shifts', detail: `${counts.ended} shift${counts.ended === 1 ? '' : 's'} will show "Mark Shift As Complete" on the locum's dashboard straight away.` }] : []),
    ...(counts.ended > 0 ? [completionRemindersSuppressed
      ? { tone: 'off', title: 'Completion reminders: OFF', detail: 'The locum is not prompted to complete the ended shifts. Tell them yourself.' }
      : { tone: 'on', title: 'Completion reminders: ON', detail: 'Within about 5 minutes the locum gets a "Completed your shift?" SMS and email for each ended shift.' }] : []),
    ...(shifts.some(shift => shift.billToMedicare) ? [{ title: 'Bill to Medicare shifts', detail: 'No Stripe invoice is created when the locum completes these. They are simply marked completed and the locum bills Medicare directly.' }] : []),
    ...(pendingRequestsToRemove.length > 0 ? [{ tone: 'warn', title: 'Clashing requests removed', detail: `${pendingRequestsToRemove.length} pending request${pendingRequestsToRemove.length === 1 ? '' : 's'} from this locum clash with these shifts and will be removed.` }] : []),
  ];
  return (
    <section className={'step-panel'}>
      <h2 className={'step-title'}>{'Review before booking'}</h2>
      <div className={'step-callout'} role={'note'}>
        <p><strong>{'Nothing has been booked yet.'}</strong>{` Check the details below, then click "Book ${counts.total} shift${counts.total === 1 ? '' : 's'}" at the bottom to confirm. Use "Back to edit" to change anything.`}</p>
      </div>
      <LocumCard locum={locum} />
      { stores.map(store => <StoreCard key={store._id} store={store} showProviderNumber={true} />) }
      <StatTiles
        tiles={[
          { value: counts.total, label: counts.total === 1 ? 'shift' : 'shifts', hint: `${stores.length} store${stores.length === 1 ? '' : 's'}` },
          { value: counts.ended, label: 'already ended', hint: 'Locum can mark complete and invoice' },
          { value: counts.upcoming, label: 'upcoming' },
          { value: formatMoney(totalValue), label: 'total shift value', hint: 'Excludes Medicare shifts' },
          ...(allWarnings.length > 0 ? [{ value: allWarnings.length, label: allWarnings.length === 1 ? 'warning' : 'warnings', hint: 'See "Check before booking" below', tone: 'warn' }] : []),
        ]}
      />
      <div className={'rows-table-wrapper'}>
        <table className={'rows-table readonly'}>
          <thead>
            <tr><th>{'#'}</th><th>{'Store'}</th><th>{'When (store time)'}</th><th>{'Break'}</th><th>{'Rate'}</th><th>{'Total'}</th><th>{'Status'}</th></tr>
          </thead>
          <tbody>
            { shifts.map(shift => (
              <tr key={shift.shiftId}>
                <td>{shift.row + 1}</td>
                <td className={'store-cell'}>{shift.storeName}</td>
                <td>
                  {formatInTimeZone(shift.startTime, shift.timeZone, 'EEE d MMM, h:mm a')}{' - '}{formatInTimeZone(shift.endTime, shift.timeZone, 'h:mm a')}
                  <PhaseBadge phase={shift.phase} />
                </td>
                <td>{`${Math.round(shift.unpaidBreakTime / 60000)} min`}</td>
                <td>{shift.billToMedicare && shift.rate === 0 ? 'Medicare' : `${formatMoney(shift.rate)}/h`}</td>
                <td>{formatMoney(shift.total)}</td>
                <td>{shift.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3 className={'section-title'}>{'What will happen'}</h3>
      <InfoList items={whatWillHappen} />
      { allWarnings.length > 0 &&
        <div className={'warning-box'} role={'status'}>
          <h3 className={'section-title'}>{`Check before booking (${allWarnings.length})`}</h3>
          <InfoList items={allWarnings.map(warning => ({ ...warning, tone: 'warn' }))} />
        </div>
      }
      { errors.length > 0 &&
        <ul className={'error-list'} role={'alert'}>{errors.map(message => <li key={message}>{message}</li>)}</ul>
      }
      <div className={'actions'}>
        <button type={'button'} className={'secondary-button'} onClick={onBack} disabled={submitting}>{'Back to edit'}</button>
        <button type={'button'} className={'primary-button'} onClick={onConfirm} disabled={submitting}>
          {submitting ? 'Booking...' : `Book ${counts.total} shift${counts.total === 1 ? '' : 's'}`}
        </button>
      </div>
    </section>
  );
};

export const ResultStep = ({ result, onBookMore, onStartOver }) => {
  const { plan, postCommit } = result;
  const { locum, shifts, counts } = plan;
  const calendarFailures = (postCommit && postCommit.calendarFailures) || [];
  const notificationFailures = (postCommit && postCommit.notificationFailures) || [];
  return (
    <section className={'step-panel'}>
      <p className={'success-kicker'}>
        <svg className={'success-check'} viewBox={'0 0 20 20'} aria-hidden={'true'}>
          <circle cx={'10'} cy={'10'} r={'10'} />
          <path d={'M5.5 10.2 8.4 13.1 14.5 7'} />
        </svg>
        {'Booking confirmed'}
      </p>
      <h2 className={'step-title success'}>{`Booked ${counts.total} shift${counts.total === 1 ? '' : 's'} for ${getLocumName(locum)}`}</h2>
      <LocumCard locum={locum} />
      { plan.stores && plan.stores.some(store => store.willBeLinked) &&
        <div className={'inline-warning'}>{`Linked to the admin account: ${plan.stores.filter(store => store.willBeLinked).map(store => store.name).join(', ')}. Log out and back in to see them in the store list.`}</div>
      }
      <StatTiles
        tiles={[
          { value: counts.total, label: counts.total === 1 ? 'shift booked' : 'shifts booked', tone: 'success' },
          { value: counts.ended, label: 'ended', hint: 'Show "Mark Shift As Complete" on the locum\'s dashboard' },
          { value: counts.upcoming, label: 'upcoming', hint: 'In the locum\'s schedule' },
          ...(postCommit && postCommit.notifiedCount > 0 ? [{ value: postCommit.notifiedCount, label: 'notified', hint: 'Locum sent a confirmation' }] : []),
        ]}
      />
      <h3 className={'section-title'}>{'What happens next'}</h3>
      <InfoList
        items={[
          { title: 'Locum', detail: 'Finds these under their dashboard shifts. Once they complete an ended shift, a Stripe invoice is created for the store and the status moves to Invoice, then Paid when the store pays.' },
          { title: 'Store', detail: 'Is not told about the booking. The shifts show under the store as filled by this locum, and upcoming shifts send the store\'s businesses the automatic 24 hour reminder.' },
          { tone: 'warn', title: 'No undo button', detail: 'Keep the shift ids below in case anything needs to be reversed.' },
        ]}
      />
      { calendarFailures.length > 0 && <div className={'inline-warning'}>{`Calendar link could not be created for ${calendarFailures.length} shift(s). The bookings themselves are saved.`}</div> }
      { notificationFailures.length > 0 && <div className={'inline-warning'}>{`Notification failed for ${notificationFailures.length} shift(s). The bookings themselves are saved.`}</div> }
      <div className={'rows-table-wrapper'}>
        <table className={'rows-table readonly'}>
          <thead>
            <tr><th>{'Store'}</th><th>{'When (store time)'}</th><th>{'Status'}</th><th>{'Shift id'}</th></tr>
          </thead>
          <tbody>
            { shifts.map(shift => (
              <tr key={shift.shiftId}>
                <td className={'store-cell'}>{shift.storeName}</td>
                <td>
                  {formatInTimeZone(shift.startTime, shift.timeZone, 'EEE d MMM, h:mm a')}
                  <PhaseBadge phase={shift.phase} />
                </td>
                <td>{shift.status}</td>
                <td className={'id-cell'}>{shift.shiftId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={'actions'}>
        <button type={'button'} className={'secondary-button'} onClick={onStartOver}>{'Start over'}</button>
        <button type={'button'} className={'primary-button'} onClick={onBookMore}>{`Book more for ${locum.firstName}`}</button>
      </div>
    </section>
  );
};
